import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { 
    Mail, Phone, User, MapPin, MessageSquare, ChevronDown, CheckCircle, 
    TrendingUp, Globe, Target, Star, ArrowRight, Building, GraduationCap, 
    Zap, Rocket, Headphones, Clock, DollarSign, ShieldCheck, BookOpen, X, Maximize2,
    Users, Award, Lightbulb, BarChart3
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import FranchiseHeroSlider from '../components/FranchiseHeroSlider';
import api from '../utils/axios';

const FeatureCard = ({ icon, title, children, delay = 0 }) => (
    <motion.div 
        className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5, delay }}
    >
        <div className="flex items-center justify-center h-16 w-16 mb-6 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-lg">
            {icon}
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{title}</h3>
        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{children}</p>
    </motion.div>
);

const Step = ({ number, title, description, icon, delay = 0 }) => (
    <motion.div 
        className="flex items-start space-x-6"
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.5, delay }}
    >
        <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white flex-shrink-0 shadow-lg">
            {icon}
        </div>
        <div className="flex-1">
            <div className="flex items-center mb-3">
                <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 font-bold text-sm mr-4">
                    {number}
                </span>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{description}</p>
        </div>
    </motion.div>
);

const FAQItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <motion.div 
            className="border-b border-gray-200 dark:border-gray-700 py-6"
            layout
        >
            <motion.button 
                className="w-full flex justify-between items-center text-left"
                onClick={() => setIsOpen(!isOpen)}
            >
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{question}</h4>
                <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <ChevronDown className="w-6 h-6 text-gray-500" />
                </motion.div>
            </motion.button>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginTop: '16px' }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    className="text-gray-600 dark:text-gray-400 leading-relaxed"
                >
                    {answer}
                </motion.div>
            )}
        </motion.div>
    );
};

const FranchiseForm = ({ register, handleSubmit, errors, isSubmitSuccessful, onSubmit, states }) => {
    const Input = ({ id, name, type, placeholder, icon, validation, error }) => (
        <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                {icon}
            </div>
            <input 
                id={id}
                type={type}
                placeholder={placeholder}
                {...register(name, validation)}
                className={`w-full pl-12 pr-4 py-3 rounded-xl bg-white dark:bg-gray-800 border-2 transition-all duration-300
                    ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 dark:border-gray-700 focus:border-primary-500 focus:ring-primary-500/20'}
                    text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 text-sm`}
            />
             {error && <p className="text-red-500 text-xs mt-1">{error.message}</p>}
        </div>
    );
    
    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-white mb-1">
                        Franchise Enquiry Form
                    </h2>
                     </div>
                
                        </div>

            <div className="p-4 relative">
                            <AnimatePresence>
                                {isSubmitSuccessful && (
                                     <motion.div 
                            className="absolute inset-0 bg-green-500/95 backdrop-blur-sm flex flex-col items-center justify-center text-white p-4 z-20 rounded-2xl"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.8 }}
                                    >
                            <CheckCircle className="w-12 h-12 mb-3"/>
                            <h3 className="text-xl font-bold mb-2">Thank You!</h3>
                            <p className="text-center text-sm">Your franchise inquiry has been received. Our team will contact you within 24 hours.</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                                    <div className="space-y-3">
                                <Input 
                                    id="name"
                                    name="name" 
                                    type="text" 
                                    placeholder="Full Name"
                        icon={<User className="w-4 h-4 text-gray-400"/>}
                                    validation={{ required: 'Full name is required' }}
                                    error={errors.name}
                                />
                                <Input 
                                    id="email"
                                    name="email" 
                                    type="email" 
                                    placeholder="Email Address"
                        icon={<Mail className="w-4 h-4 text-gray-400"/>}
                                    validation={{ required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' }}}
                                    error={errors.email}
                                />
                                <Input 
                                    id="phone"
                                    name="phone"
                                    type="tel" 
                                    placeholder="Phone Number"
                        icon={<Phone className="w-4 h-4 text-gray-400"/>}
                                    validation={{ required: 'Phone number is required' }}
                                    error={errors.phone}
                                />
                </div>

                <div className="space-y-3">
                    <div className="relative">
                        <MapPin className="w-4 h-4 text-gray-400 absolute top-3 left-4 pointer-events-none"/>
                        <select
                            id="state"
                            {...register('state', { required: 'Please select a state' })}
                            className={`w-full appearance-none pl-12 pr-10 py-3 rounded-xl bg-white dark:bg-gray-800 border-2 transition-all duration-300
                                ${errors.state ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 dark:border-gray-700 focus:border-primary-500 focus:ring-primary-500/20'}
                                text-gray-900 dark:text-white focus:outline-none focus:ring-2 text-sm`}
                        >
                            <option value="">Select State...</option>
                            {states.map((state) => (
                                <option key={state} value={state}>{state}</option>
                            ))}
                        </select>
                        <ChevronDown className="w-4 h-4 text-gray-400 absolute top-3 right-4 pointer-events-none"/>
                        {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state.message}</p>}
                    </div>
                                <Input 
                        id="city"
                        name="city"
                                    type="text" 
                        placeholder="City"
                        icon={<MapPin className="w-4 h-4 text-gray-400"/>}
                        validation={{ required: 'City is required' }}
                        error={errors.city}
                    />
                </div>

                <div className="space-y-3">
                    <div className="relative">
                        <DollarSign className="w-4 h-4 text-gray-400 absolute top-3 left-4 pointer-events-none"/>
                        <select
                            id="investment"
                            {...register('investment', { required: 'Please select investment range' })}
                            className={`w-full appearance-none pl-12 pr-10 py-3 rounded-xl bg-white dark:bg-gray-800 border-2 transition-all duration-300
                                ${errors.investment ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 dark:border-gray-700 focus:border-primary-500 focus:ring-primary-500/20'}
                                text-gray-900 dark:text-white focus:outline-none focus:ring-2 text-sm`}
                        >
                            <option value="">Select Investment Range...</option>
                            <option value="5L-8L">₹5L - ₹8L (Tier 3)</option>
                            <option value="8L-12L">₹8L - ₹12L (Tier 2)</option>
                            <option value="12L-15L">₹12L - ₹15L (Tier 1)</option>
                            <option value="15L+">₹15L+ (Premium)</option>
                        </select>
                        <ChevronDown className="w-4 h-4 text-gray-400 absolute top-3 right-4 pointer-events-none"/>
                        {errors.investment && <p className="text-red-500 text-xs mt-1">{errors.investment.message}</p>}
                    </div>
                    
                    <div>
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Do you own a commercial space?</p>
                        <div className="flex space-x-4">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="commercialSpace"
                                    value="yes"
                                    {...register('commercialSpace', { required: 'Please select an option' })}
                                    className="w-3 h-3 text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500"
                                />
                                <span className="ml-2 text-xs text-gray-700 dark:text-gray-300">Yes</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="commercialSpace"
                                    value="no"
                                    {...register('commercialSpace', { required: 'Please select an option' })}
                                    className="w-3 h-3 text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500"
                                />
                                <span className="ml-2 text-xs text-gray-700 dark:text-gray-300">No</span>
                            </label>
                        </div>
                        {errors.commercialSpace && <p className="text-red-500 text-xs mt-1">{errors.commercialSpace.message}</p>}
                    </div>
                    
                                
                </div>

                    <button 
                        type="submit" 
                        className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-sm"
                    >
                        Submit Franchise Enquiry
                    </button>
                </form>


            </div>
        </div>
    );
};

const Franchise = () => {
    const { register, handleSubmit, formState: { errors, isSubmitSuccessful }, reset, watch } = useForm();
    const { isDark } = useTheme();
    const selectedState = watch('state');

    const onSubmit = async (data) => {
        try {
            // Validate required fields
            const requiredFields = ['name', 'email', 'phone', 'state', 'city', 'investment', 'commercialSpace'];
            const missingFields = requiredFields.filter(field => !data[field]);
            
            if (missingFields.length > 0) {
                        return;
      }
            
            // Format data for better logging
            const formattedData = {
                name: data.name,
                email: data.email,
                phone: data.phone,
                state: data.state,
                city: data.city,
                investment: data.investment,
                commercialSpace: data.commercialSpace,
                message: data.message || ''
            };
            
            const response = await api.post('/franchise-enquiries', formattedData);
            setTimeout(() => reset(), 3000);
        } catch (error) {
            // The form will handle the error display
        }
    };

    const states = [
        'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 
        'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 
        'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 
        'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 
        'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Chandigarh', 
        'Dadra & Nagar Haveli', 'Daman & Diu', 'Lakshadweep', 'Puducherry', 'Andaman & Nicobar Islands'
    ];
    
    return (
        <div className={`pt-20 ${isDark ? 'dotted-bg-dark' : 'dotted-bg-light'}`}>
            {/* Main Content - Full Width with Right Margin for Form */}
            <div className="max-w-7xl mx-auto px-6 lg:pr-96 pb-20">
                {/* Form - Right Side (Desktop) - Sticky Position */}
                <div className="lg:sticky lg:top-20 lg:right-0 lg:w-96 z-50 lg:float-right lg:ml-12 lg:-mr-[28rem]">
                    <FranchiseForm 
                        register={register}
                        handleSubmit={handleSubmit}
                        errors={errors}
                        isSubmitSuccessful={isSubmitSuccessful}
                        onSubmit={onSubmit}
                        states={states}
                    />
                </div>
                {/* Hero Section */}
                <section className="py-12 lg:py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center"
                    >
                        {/* Main Heading */}
                        <div className="mb-8 relative">
                            {/* Background Gradient Effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-purple-500/10 to-blue-500/10 rounded-3xl blur-3xl"></div>
                            
                            {/* Content */}
                            <div className="relative z-10">
                                <div className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-primary-500 to-purple-600 text-white text-sm font-semibold rounded-full mb-6 shadow-lg">
                                    <Star className="w-4 h-4 mr-2" />
                                    #1 Tech Education Franchise
                                </div>
                                
                                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                                    Fuel Your Success — 
                                    <span className="block bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
                                        Become an OSOPCoding Franchise Partner
                                    </span>
                                </h1>
                                
                                <p className="text-xl lg:text-2xl text-gray-600 dark:text-gray-400 max-w-4xl mx-auto leading-relaxed mb-8">
                                    Join a network that's revolutionizing tech education with proven success and exponential growth potential.
                                </p>
                                

                            </div>
                        </div>

                        {/* Hero Image Slider */}
                        <div className="mb-12 relative">
                            {/* Decorative Elements */}
                            <div className="absolute -top-4 -left-4 w-8 h-8 bg-gradient-to-br from-primary-500 to-purple-600 rounded-full opacity-20 animate-pulse"></div>
                            <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full opacity-20 animate-pulse delay-1000"></div>
                            <div className="absolute top-1/2 -right-8 w-6 h-6 bg-gradient-to-br from-green-500 to-blue-600 rounded-full opacity-30 animate-bounce"></div>
                            
                            {/* Enhanced Slider Container */}
                            <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-purple-500/5 rounded-3xl"></div>
                                <div className="relative z-10">
                                    <FranchiseHeroSlider />
                                </div>
                            </div>
                            
                            {/* Floating Action Button */}
                            <div className="absolute bottom-4 right-4 z-20">
                                <button className="bg-gradient-to-r from-primary-500 to-purple-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110">
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* City Tier Requirements */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="max-w-6xl mx-auto"
                        >
                            <div className="text-center mb-8">
                                <div className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm font-semibold rounded-full mb-4 shadow-lg">
                                    <Globe className="w-4 h-4 mr-2" />
                                    Global Expansion
                                </div>
                                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">City Requirements</h3>
                                <p className="text-xl text-gray-600 dark:text-gray-400">🚀 Launch Your Own Tech Education Empire in Your City!</p>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {/* Tier 1 Cities */}
                                <motion.div 
                                    className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-8 rounded-2xl border border-purple-200 dark:border-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.4 }}
                                >
                                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl mb-6 mx-auto">
                                        <Star className="w-8 h-8 text-white" />
                                    </div>
                                    <h4 className="text-2xl font-bold text-purple-900 dark:text-purple-100 mb-4 text-center">Tier 1 Cities</h4>
                                    <div className="space-y-4 text-center">
                                        <div>
                                            <div className="text-lg font-semibold text-purple-800 dark:text-purple-200">Investment</div>
                                            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">₹12L-15L</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-semibold text-purple-800 dark:text-purple-200">Franchise Fee</div>
                                            <div className="text-xl font-bold text-purple-900 dark:text-purple-100">₹2.5 Lakhs</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-semibold text-purple-800 dark:text-purple-200">Royalty Fee</div>
                                            <div className="text-xl font-bold text-purple-900 dark:text-purple-100">15% of Revenue</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-semibold text-purple-800 dark:text-purple-200">Space Required</div>
                                            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">1200-1500 sq ft</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-semibold text-purple-800 dark:text-purple-200">Breakeven Timeline</div>
                                            <div className="text-xl font-bold text-purple-900 dark:text-purple-100">24-30 Months</div>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Tier 2 Cities */}
                                <motion.div 
                                    className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-8 rounded-2xl border border-blue-200 dark:border-blue-700 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.6 }}
                                >
                                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-6 mx-auto">
                                        <Target className="w-8 h-8 text-white" />
                                    </div>
                                    <h4 className="text-2xl font-bold text-blue-900 dark:text-blue-100 mb-4 text-center">Tier 2 Cities</h4>
                                    <div className="space-y-4 text-center">
                                        <div>
                                            <div className="text-lg font-semibold text-blue-800 dark:text-blue-200">Investment</div>
                                            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">₹8L-12L</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-semibold text-blue-800 dark:text-blue-200">Franchise Fee</div>
                                            <div className="text-xl font-bold text-blue-900 dark:text-blue-100">₹1.75 Lakhs</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-semibold text-blue-800 dark:text-blue-200">Royalty Fee</div>
                                            <div className="text-xl font-bold text-blue-900 dark:text-blue-100">15% of Revenue</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-semibold text-blue-800 dark:text-blue-200">Space Required</div>
                                            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">1200-1500 sq ft</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-semibold text-blue-800 dark:text-blue-200">Breakeven Timeline</div>
                                            <div className="text-xl font-bold text-blue-900 dark:text-blue-100">24-30 Months</div>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Tier 3 Cities */}
                                <motion.div 
                                    className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-8 rounded-2xl border border-green-200 dark:border-green-700 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.8 }}
                                >
                                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl mb-6 mx-auto">
                                        <Rocket className="w-8 h-8 text-white" />
                                    </div>
                                    <h4 className="text-2xl font-bold text-green-900 dark:text-green-100 mb-4 text-center">Tier 3 Cities</h4>
                                    <div className="space-y-4 text-center">
                                        <div>
                                            <div className="text-lg font-semibold text-green-800 dark:text-green-200">Investment</div>
                                            <div className="text-2xl font-bold text-green-900 dark:text-green-100">₹5L-8L</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-semibold text-green-800 dark:text-green-200">Franchise Fee</div>
                                            <div className="text-xl font-bold text-green-900 dark:text-green-100">₹1 Lakh</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-semibold text-green-800 dark:text-green-200">Royalty Fee</div>
                                            <div className="text-xl font-bold text-green-900 dark:text-green-100">15% of Revenue</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-semibold text-green-800 dark:text-green-200">Space Required</div>
                                            <div className="text-2xl font-bold text-green-900 dark:text-green-100">800-1200 sq ft</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-semibold text-green-800 dark:text-green-200">Breakeven Timeline</div>
                                            <div className="text-xl font-bold text-green-900 dark:text-green-100">24-30 Months</div>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </motion.div>
                    </motion.div>
                </section>



                {/* Benefits Section */}
                <section className="py-16 bg-gray-50 dark:bg-gray-900/50 rounded-3xl my-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                            Why Partner with OSOPCoding?
                        </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-400 mb-12 text-center max-w-3xl mx-auto">
                            Tap into a thriving industry with a brand built for success. OSOPCoding empowers franchise partners with a proven model, exceptional support, and unlimited growth potential.
                        </p>
                    </motion.div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto px-6">
                        <FeatureCard 
                            icon={<ShieldCheck className="w-6 h-6" />}
                            title="Proven Business Model"
                            delay={0.1}
                        >
                            With OSOPCoding's proven curriculum and brand recognition, franchisees can start with a clear, effective strategy for success.
                        </FeatureCard>
                        <FeatureCard 
                            icon={<Headphones className="w-6 h-6" />}
                            title="Strong Support System"
                            delay={0.2}
                        >
                            Franchisees benefit from a dedicated support system, including staff training, marketing assistance, and ongoing business guidance.
                        </FeatureCard>
                        <FeatureCard 
                            icon={<TrendingUp className="w-6 h-6" />}
                            title="Growth Potential"
                            delay={0.3}
                        >
                            The technology and digital skills sector is growing exponentially. As demand for coding and IT professionals rises, so does the potential for franchise success.
                        </FeatureCard>
                        <FeatureCard 
                            icon={<Star className="w-6 h-6" />}
                            title="High Financial Returns"
                            delay={0.4}
                        >
                            OSOPCoding franchises have a high ROI potential, with the possibility of generating impressive returns through scalable business operations.
                        </FeatureCard>
                    </div>
                </section>

                {/* How to Become Section */}
                <section className="py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                            How to Become an OSOPCoding Franchisee
                        </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-400 mb-12 text-center max-w-3xl mx-auto">
                            Your journey to becoming a successful tech education entrepreneur starts here.
                        </p>
                    </motion.div>
                    
                    <div className="max-w-4xl mx-auto space-y-8">
                        <Step 
                            number="1" 
                            title="Research & Exploration" 
                            description="Begin by exploring OSOPCoding's various franchise options, including specialized coding programs, IT certifications, and technical workshops."
                            icon={<BookOpen className="w-6 h-6" />}
                            delay={0.1}
                        />
                        <Step 
                            number="2" 
                            title="Investment Planning" 
                            description="Ensure you're prepared to invest in the franchise fee, training, infrastructure, and initial setup costs ranging from ₹5L to ₹15L."
                            icon={<DollarSign className="w-6 h-6" />}
                            delay={0.2}
                        />
                        <Step 
                            number="3" 
                            title="Location Selection" 
                            description="Choose a prime location with access to target demographics, preferably in high-traffic areas near educational hubs or business districts."
                            icon={<MapPin className="w-6 h-6" />}
                            delay={0.3}
                        />
                        <Step 
                            number="4" 
                            title="Comprehensive Training" 
                            description="Participate in a comprehensive training program to get you acquainted with OSOPCoding's curriculum, technology, and operations."
                            icon={<GraduationCap className="w-6 h-6" />}
                            delay={0.4}
                        />
                        <Step 
                            number="5" 
                            title="Launch & Ongoing Support" 
                            description="After launch, OSOPCoding continues to offer support in areas like marketing, course updates, and business growth strategies."
                            icon={<Rocket className="w-6 h-6" />}
                            delay={0.5}
                        />
                     </div>
                </section>
                
                {/* FAQ Section */}
                <section className="py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-400 mb-12 text-center max-w-3xl mx-auto">
                            Get answers to the most common questions about OSOPCoding franchise opportunities.
                        </p>
                    </motion.div>
                    
                    <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
                        <FAQItem question="What is the initial investment required for an OSOPCoding franchise?">
                            The initial investment for an OSOPCoding franchise ranges from ₹5 lakhs to ₹15 lakhs, depending on the location, center size, and other setup costs. This includes the franchise fee, infrastructure, and operational setup.
                        </FAQItem>
                        <FAQItem question="What are the space requirements for a franchise center?">
                            A minimum space of 800-1500 sq ft is required to ensure a well-equipped learning environment with modern facilities.
                        </FAQItem>
                        <FAQItem question="Do I need a technical background to run an OSOPCoding franchise?">
                            While a passion for education is essential, a technical background is not mandatory. We provide comprehensive training for our curriculum and business operations to ensure your success.
                        </FAQItem>
                        <FAQItem question="How long does it take to open an OSOPCoding franchise center?">
                            Typically, it takes about 2-3 months from signing the agreement to the grand opening of your center, including training, setup, and preparation.
                        </FAQItem>
                        <FAQItem question="What kind of support does OSOPCoding provide to franchisees?">
                            We offer comprehensive pre-launch and ongoing training, marketing support, operational guidance, curriculum updates, and continuous business development assistance.
                        </FAQItem>
                        <FAQItem question="What are the growth prospects for an OSOPCoding franchise?">
                            The technology and digital skills sector is growing exponentially. With increasing demand for coding and IT professionals, OSOPCoding franchises have excellent growth potential and high ROI opportunities.
                         </FAQItem>
                         <FAQItem question="What is the royalty fee structure for OSOPCoding franchises?">
                            All OSOPCoding franchises have a royalty fee of 15% of revenue, which helps maintain quality standards and provides ongoing support and updates.
                         </FAQItem>
                         <FAQItem question="How long does it take to break even on the investment?">
                            The breakeven timeline for OSOPCoding franchises is typically 24-30 months, depending on location, market conditions, and operational efficiency.
                         </FAQItem>
                     </div>
                </section>
            </div>
        </div>
    );
};

export default Franchise;